import React from 'react';
import { styled } from '@compiled/react';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import QueuesIcon from '@atlaskit/icon/core/pages';
import { token } from '@atlaskit/tokens';
import {
	BOARDS_ITEM_TYPE,
	DASHBOARDS_ITEM_TYPE,
	FILTERS_ITEM_TYPE,
	ISSUES_ITEM_TYPE,
	QUEUES_ITEM_TYPE,
	PLANS_ITEM_TYPE,
} from '../../../../constants/item-types.tsx';

export type Props = {
	type: string | undefined;
	url: string | undefined;
	altText?: string;
};

// request to include icon in atlaskit
// https://ecosystem.atlassian.net/jira/servicedesk/projects/DS/queues/issue/DS-7743
// https://jdog.jira-dev.com/browse/JPO-18296
const PlansIcon = () => (
	<svg height={24} width={24} fill="none" viewBox="0 0 24 24">
		<path
			fill={token('color.icon')}
			d="M10 8a1 1 0 000 2h4a1 1 0 100-2h-4zM10 12a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zM10 14a1 1 0 100 2h2a1 1 0 100-2h-2z"
		/>
		<path
			fill={token('color.icon')}
			fillRule="evenodd"
			d="M8 4a2 2 0 012-2h4a2 2 0 012 2h1a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2h1zm6 3a2 2 0 001.732-1H17v12H7V6h1.268A2 2 0 0010 7h4zm0-3h-4v1h4V4z"
			clipRule="evenodd"
		/>
	</svg>
);

const iconMap = {
	[BOARDS_ITEM_TYPE]: BoardIcon,
	[FILTERS_ITEM_TYPE]: FilterIcon,
	[DASHBOARDS_ITEM_TYPE]: DashboardIcon,
	[ISSUES_ITEM_TYPE]: WorkItemIcon,
	[QUEUES_ITEM_TYPE]: QueuesIcon,
	[PLANS_ITEM_TYPE]: PlansIcon,
} as const;

const ItemIcon = ({ type, url, altText = '' }: Props): React.JSX.Element => {
	// boards include the project avatar URL, which we don't want to show
	if (typeof url === 'string' && type !== BOARDS_ITEM_TYPE) {
		return (
			<AvatarIcon>
				<Avatar src={url} alt={altText} />
			</AvatarIcon>
		);
	}

	if (!type) {
		return <div />;
	}

	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly board: typeof default; readonly filter: typeof default; readonly dashboard: typeof default; readonly issue: typeof default; readonly queue: typeof default; readonly plan: () => Element; }'.
	const Icon = iconMap[type];

	if (!Icon) {
		return <div />;
	}

	return (
		<AvatarIcon>
			<Icon color={token('color.icon')} spacing="spacious" />
		</AvatarIcon>
	);
};

export default ItemIcon;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Avatar = styled.img({
	height: '24px',
	width: '24px',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	display: 'block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarIcon = styled.div({
	height: '24px',
	width: '24px',
	display: 'block',
});
